import { useEffect, useReducer, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Link, useLocation, useRoute } from "wouter";
import { useTranslation } from "react-i18next";
import { Sakura } from "../components/shrine/Sakura";
import { OmikujiCard } from "../components/omikuji/OmikujiCard";
import { useSound } from "../hooks/useSound";
import { useVibration, VibrationPatterns } from "../hooks/useVibration";
import { appendDraw, ensureSeedPair } from "../lib/storage";
import type { DrawResult } from "../lib/omikuji";
import { drawOmikuji } from "../lib/omikuji";
import { PAIR_DRAW_SESSION_KEY } from "./PairRitual";

/**
 * §2.5 同時開示画面
 *  countdown 三・二・一 → 札がひらりと開く → 結果 → コレクションへ
 */

type PendingDraw = {
  pairId: string;
  partnerName: string;
  yourResult: DrawResult;
  partnerResult: DrawResult;
  startedAt: string;
};

type Step = "countdown" | "open" | "result" | "done";

function reducer(s: { step: Step; cardState: "closed" | "opening" | "opened" }, a: { type: "next" } | { type: "card"; v: "closed" | "opening" | "opened" }) {
  if (a.type === "next") {
    const next: Record<Step, Step> = {
      countdown: "open",
      open: "result",
      result: "done",
      done: "done",
    };
    return { ...s, step: next[s.step] };
  }
  return { ...s, cardState: a.v };
}

export function SimultaneousReveal() {
  const { t, i18n } = useTranslation();
  const [, params] = useRoute("/pair/:pairId/reveal");
  const [, navigate] = useLocation();
  const reduce = useReducedMotion();
  const { unlock, play } = useSound();
  const { vibrate } = useVibration();

  const [pending, setPending] = useState<PendingDraw | null>(null);
  const [{ step, cardState }, dispatch] = useReducer(reducer, { step: "countdown", cardState: "closed" });
  const savedRef = useRef(false);

  // sessionStorage から復元 (or fresh fallback)
  useEffect(() => {
    ensureSeedPair();
    const pairId = params?.pairId ?? "seed_yu";
    let p: PendingDraw | null = null;
    try {
      const raw = sessionStorage.getItem(PAIR_DRAW_SESSION_KEY);
      if (raw) p = JSON.parse(raw) as PendingDraw;
    } catch {
      /* noop */
    }
    if (!p || p.pairId !== pairId) {
      // 直接URL叩いた場合のフォールバック
      p = {
        pairId,
        partnerName: "ゆう",
        yourResult: drawOmikuji({ partnerName: "ゆう" }),
        partnerResult: drawOmikuji({ partnerName: "あなた" }),
        startedAt: new Date().toISOString(),
      };
    }
    setPending(p);
  }, [params?.pairId]);

  // 自動進行: countdown(3.5s) → open → result(5s) → done
  useEffect(() => {
    if (!pending) return;
    if (step === "countdown") {
      const id = window.setTimeout(() => dispatch({ type: "next" }), reduce ? 600 : 3500);
      return () => clearTimeout(id);
    }
    if (step === "open") {
      unlock();
      dispatch({ type: "card", v: "opening" });
      play("cardOpen");
      vibrate(VibrationPatterns.cardOpen);
      const id1 = window.setTimeout(() => dispatch({ type: "card", v: "opened" }), 600);
      const id2 = window.setTimeout(() => dispatch({ type: "next" }), reduce ? 700 : 1800);
      // 大吉以上は太鼓
      const r = pending.yourResult.rarity;
      if (r === "dai_kichi" || r === "tokudai_kichi") {
        window.setTimeout(() => {
          play("taikoDon");
          vibrate(VibrationPatterns.daiKichi);
        }, 800);
      }
      return () => {
        clearTimeout(id1);
        clearTimeout(id2);
      };
    }
    if (step === "result") {
      // ストアに保存 (1度だけ)
      if (!savedRef.current && pending) {
        savedRef.current = true;
        appendDraw({
          mode: "pair",
          pairId: pending.pairId,
          rarity: pending.yourResult.rarity,
          bodyText: pending.yourResult.bodyText,
          bodyTextEn: pending.yourResult.bodyTextEn,
          themeAuto: pending.yourResult.themeAuto,
          partnerName: pending.partnerName,
        });
      }
      const id = window.setTimeout(() => dispatch({ type: "next" }), reduce ? 800 : 5500);
      return () => clearTimeout(id);
    }
    if (step === "done") {
      try {
        sessionStorage.removeItem(PAIR_DRAW_SESSION_KEY);
      } catch {
        /* noop */
      }
    }
  }, [step, pending, reduce, play, vibrate, unlock]);

  if (!pending) {
    return <main className="min-h-dvh bg-[var(--color-night-deep)]" />;
  }

  return (
    <main className="relative min-h-dvh overflow-hidden bg-[var(--color-night-deep)] text-[var(--color-washi)]">
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at center 80%, rgba(159,29,53,0.18), transparent 60%), linear-gradient(to bottom, #050203 0%, #0a0608 50%, #1a0c12 100%)",
        }}
      />
      <Sakura count={12} />

      <div className="relative z-10 min-h-dvh flex flex-col items-center justify-center px-4 py-8 gap-8">
        <AnimatePresence mode="wait">
          {step === "countdown" && <Countdown key="cd" />}

          {step !== "countdown" && (
            <motion.div
              key="cards"
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="flex gap-3 items-center"
            >
              <OmikujiCard
                rarity={pending.yourResult.rarity}
                body={i18n.language === "en" ? pending.yourResult.bodyTextEn : pending.yourResult.bodyText}
                state={cardState}
                position="left"
                size={150}
                ownerLabel={t("welcome.title")}
              />
              <OmikujiCard
                rarity={pending.partnerResult.rarity}
                body={
                  i18n.language === "en"
                    ? pending.partnerResult.bodyTextEn
                    : pending.partnerResult.bodyText
                }
                state={cardState}
                position="right"
                size={150}
                ownerLabel={pending.partnerName}
              />
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {step === "result" && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.8 }}
              className="text-sm tracking-widest text-[var(--color-gold-light)]"
              style={{ fontFamily: "var(--font-fude)" }}
            >
              {t("pair.bothReady")}
            </motion.p>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {step === "done" && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex flex-col items-center gap-3"
            >
              <p className="text-sm tracking-widest text-[var(--color-gold-light)]" style={{ fontFamily: "var(--font-fude)" }}>
                ✦ {t("ritual.added")} ✦
              </p>
              <Link
                href="/home"
                className="mt-4 px-8 py-3 rounded-full text-[var(--color-washi)] tracking-[0.3em] text-xs"
                style={{
                  background: "linear-gradient(180deg, #c43a55 0%, #9f1d35 50%, #6b1024 100%)",
                  border: "1px solid rgba(201,169,97,0.7)",
                  boxShadow: "0 0 24px rgba(159,29,53,0.45)",
                }}
                onClick={() => navigate("/home")}
              >
                {t("common.next")}
              </Link>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </main>
  );
}

function Countdown() {
  const [n, setN] = useState(3);
  useEffect(() => {
    if (n <= 0) return;
    const id = window.setTimeout(() => setN(n - 1), 1000);
    return () => clearTimeout(id);
  }, [n]);
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={n}
        initial={{ opacity: 0, scale: 2 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.4 }}
        transition={{ duration: 0.6 }}
        className="text-9xl"
        style={{
          fontFamily: "var(--font-fude)",
          color: "var(--color-gold-light)",
          textShadow: "0 0 32px rgba(201,169,97,0.6)",
        }}
      >
        {n > 0 ? ["", "一", "二", "三"][n] : "✦"}
      </motion.div>
    </AnimatePresence>
  );
}
